import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Box, Stack, Typography, alpha, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import strings from 'common/Translation/Translate';
import type { ReactNode } from 'react';

export type PricingInsightsData = {
  similarEventsAverage?: number | null;
  formattedSimilarEventsAverage?: string | null;
  estimateVsAveragePercent?: number | null;
  similarEventsSampleSize?: number | null;
  guestRangeMin?: number | null;
  guestRangeMax?: number | null;
  venueName?: string | null;
  partOfDayName?: string | null;
  eventTypeName?: string | null;
  similarEventsInfo?: string | null;
  revenuePerGuest?: number | null;
  formattedRevenuePerGuest?: string | null;
  similarEventsAverageRevenuePerGuest?: number | null;
  formattedSimilarEventsAverageRevenuePerGuest?: string | null;
  revenuePerGuestVsAveragePercent?: number | null;
  standardValue?: number | null;
  formattedStandardValue?: string | null;
  currentValue?: number | null;
  formattedCurrentValue?: string | null;
  discountAmount?: number | null;
  formattedDiscountAmount?: string | null;
  discountPercent?: number | null;
};

type PricingInsightsProps = {
  insights: PricingInsightsData;
};

type TrendTone = 'up' | 'down' | 'flat';
type BenchmarkMode = 'insufficient' | 'limited' | 'full';

const resolveBenchmarkMode = (sampleSize: number | null | undefined): BenchmarkMode => {
  const count = sampleSize ?? 0;
  if (count < 3) return 'insufficient';
  if (count < 5) return 'limited';
  return 'full';
};

const resolveTone = (percent: number | null | undefined): TrendTone => {
  if (percent == null || Number.isNaN(percent)) return 'flat';
  if (percent < -0.05) return 'down';
  if (percent > 0.05) return 'up';
  return 'flat';
};

const toneColor = (theme: Theme, tone: TrendTone) => {
  if (tone === 'down') return theme.palette.error.main;
  if (tone === 'up') return theme.palette.success.main;
  return theme.palette.text.secondary;
};

/**
 * 0–99%   → "14% above average"
 * 100–199% → "185% above average"
 * 200%+    → "6.4× average"  (ratio = 1 + abs%/100)
 */
const formatBenchmarkDelta = (percent: number, tone: TrendTone): string => {
  if (tone === 'flat') return strings.pricing_insights_at_average;

  const abs = Math.abs(percent);
  if (tone === 'up' && abs >= 200) {
    const times = 1 + abs / 100;
    return String(strings.formatString(strings.pricing_insights_times_average, times.toFixed(1)));
  }

  const rounded = String(Math.round(abs));
  if (tone === 'down') {
    return String(strings.formatString(strings.pricing_insights_pct_below_average, rounded));
  }
  return String(strings.formatString(strings.pricing_insights_pct_above_average, rounded));
};

const TrendArrow = ({ tone, color }: { tone: TrendTone; color: string }) => {
  if (tone === 'down') return <ArrowDownwardIcon sx={{ fontSize: 16, color }} />;
  if (tone === 'up') return <ArrowUpwardIcon sx={{ fontSize: 16, color }} />;
  return null;
};

const IconCircle = ({
  icon,
  color,
}: {
  icon: ReactNode;
  color: string;
}) => (
  <Box
    sx={{
      width: 44,
      height: 44,
      borderRadius: '50%',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: alpha(color, 0.12),
      color,
    }}>
    {icon}
  </Box>
);

const InsightRow = ({
  icon,
  iconColor,
  title,
  primary,
  secondary,
  tertiary,
}: {
  icon: ReactNode;
  iconColor: string;
  title: string;
  primary: ReactNode;
  secondary?: ReactNode;
  tertiary?: ReactNode;
}) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start">
    <IconCircle icon={icon} color={iconColor} />
    <Box sx={{ minWidth: 0, flex: 1, pt: 0.15 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ display: 'block', lineHeight: 1.3, letterSpacing: 0.15, mb: 0.2 }}>
        {title}
      </Typography>
      {primary}
      {secondary}
      {tertiary}
    </Box>
  </Stack>
);

const PricingInsights = ({ insights }: PricingInsightsProps) => {
  const theme = useTheme();

  const sampleSize = insights.similarEventsSampleSize ?? 0;
  const benchmarkMode = resolveBenchmarkMode(sampleSize);

  const hasSimilarValues =
    sampleSize > 0 && !!insights.formattedSimilarEventsAverage && benchmarkMode !== 'insufficient';
  const hasRpg = !!insights.formattedRevenuePerGuest;
  const hasDiscount =
    insights.discountAmount != null &&
    insights.discountAmount > 0 &&
    !!insights.formattedStandardValue &&
    !!insights.formattedCurrentValue;

  if (!hasSimilarValues && !hasRpg && !hasDiscount) return null;

  const similarTone = resolveTone(insights.estimateVsAveragePercent);
  const similarColor = toneColor(theme, similarTone);
  const rpgTone = resolveTone(insights.revenuePerGuestVsAveragePercent);
  const rpgColor = toneColor(theme, rpgTone);
  const discountTone: TrendTone = 'down';
  const discountColor = theme.palette.warning.main;

  const showPercentMessaging = benchmarkMode === 'full';
  const similarPercentLabel =
    showPercentMessaging && insights.estimateVsAveragePercent != null
      ? formatBenchmarkDelta(insights.estimateVsAveragePercent, similarTone)
      : null;
  const rpgPercentLabel =
    showPercentMessaging && insights.revenuePerGuestVsAveragePercent != null && rpgTone !== 'flat'
      ? formatBenchmarkDelta(insights.revenuePerGuestVsAveragePercent, rpgTone)
      : null;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ fontSize: '0.8125rem', lineHeight: 1.3, mb: 1.25 }}>
        {strings.pricing_insights_title}
      </Typography>

      <Stack spacing={1.5}>
        {hasSimilarValues ? (
          <InsightRow
            icon={<GroupsOutlinedIcon sx={{ fontSize: 22 }} />}
            iconColor={theme.palette.info.main}
            title={strings.pricing_insights_similar_event_average}
            primary={
              <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ rowGap: 0.25 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.35 }}>
                  {insights.formattedSimilarEventsAverage}
                </Typography>
                {similarPercentLabel ? (
                  <Stack direction="row" alignItems="center" spacing={0.25}>
                    <TrendArrow tone={similarTone} color={similarColor} />
                    <Typography variant="caption" sx={{ color: similarColor, lineHeight: 1.2 }}>
                      {similarPercentLabel}
                    </Typography>
                  </Stack>
                ) : null}
              </Stack>
            }
            secondary={
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.35 }}>
                {benchmarkMode === 'limited'
                  ? strings.pricing_insights_limited_data
                  : strings.formatString(strings.pricing_insights_based_on_bookings, String(sampleSize))}
              </Typography>
            }
            tertiary={
              benchmarkMode === 'limited' ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15, lineHeight: 1.35 }}>
                  {strings.formatString(strings.pricing_insights_based_on_bookings, String(sampleSize))}
                </Typography>
              ) : null
            }
          />
        ) : null}

        {hasRpg ? (
          <InsightRow
            icon={<PersonOutlineOutlinedIcon sx={{ fontSize: 22 }} />}
            iconColor={theme.palette.primary.main}
            title={strings.pricing_insights_revenue_per_guest}
            primary={
              <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={0.5} sx={{ rowGap: 0.25 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.35 }}>
                  {insights.formattedRevenuePerGuest}
                </Typography>
                {showPercentMessaging ? <TrendArrow tone={rpgTone} color={rpgColor} /> : null}
                {benchmarkMode !== 'insufficient' && insights.formattedSimilarEventsAverageRevenuePerGuest ? (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    fontWeight={500}
                    sx={{ fontSize: '0.75rem' }}>
                    {strings.formatString(
                      strings.pricing_insights_avg_in_parens,
                      insights.formattedSimilarEventsAverageRevenuePerGuest,
                    )}
                  </Typography>
                ) : null}
              </Stack>
            }
            secondary={
              rpgPercentLabel ? (
                <Typography variant="caption" sx={{ color: rpgColor, display: 'block', mt: 0.35, lineHeight: 1.2 }}>
                  {rpgPercentLabel}
                </Typography>
              ) : benchmarkMode === 'limited' ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.35 }}>
                  {strings.pricing_insights_limited_data}
                </Typography>
              ) : null
            }
          />
        ) : null}

        {hasDiscount ? (
          <InsightRow
            icon={<LocalOfferOutlinedIcon sx={{ fontSize: 22 }} />}
            iconColor={discountColor}
            title={strings.pricing_insights_discount_impact}
            primary={
              <Typography variant="body2" sx={{ fontSize: '0.8125rem', lineHeight: 1.35 }}>
                {insights.formattedCurrentValue}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  fontWeight={500}
                  sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                  {strings.formatString(
                    strings.pricing_insights_std_in_parens,
                    insights.formattedStandardValue ?? '',
                  )}
                </Typography>
              </Typography>
            }
            secondary={
              <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mt: 0.35 }}>
                <TrendArrow tone={discountTone} color={discountColor} />
                <Typography variant="caption" sx={{ color: discountColor, lineHeight: 1.2 }}>
                  {strings.formatString(
                    strings.pricing_insights_discount_delta,
                    Math.abs(insights.discountPercent ?? 0).toFixed(1),
                    insights.formattedDiscountAmount ?? '',
                  )}
                </Typography>
              </Stack>
            }
          />
        ) : null}
      </Stack>
    </Box>
  );
};

export default PricingInsights;
